import { Link } from 'react-router-dom';
import {
  UXP_CO_ID,
  EXPLORER_FEATURES,
  LIFECYCLE_STAGES,
  RELEASE_CONTENTS,
  VERSION_FEATURES,
  DRAFT_CAPABILITIES,
  REVIEW_TYPES,
  SIMULATION_METRICS,
  VALIDATION_CHECKS,
  ADVISOR_RECOMMENDATIONS,
  SCHEDULING_MODES,
  ROLLBACK_SCOPES,
  PUBLISH_JOB_STATES,
  SUPPRESSION_FEATURES,
  BULK_OPERATIONS,
  COLLABORATION_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  ANALYTICS_METRICS,
  LIVE_ROUTES,
  DEMO_RELEASES,
  DEMO_SIMULATION,
  DEMO_PUBLISH_JOBS,
  DEMO_SUPPRESSIONS,
  DEMO_REVIEW_QUEUE,
  DEMO_ADVISOR_INSIGHTS,
  buildCatalogOpsDashboard,
} from '../../cataloglifecycle/framework';
import {
  CatalogExplorerPanel,
  LifecycleStageTracker,
  ReleasePackageCard,
  VersionControlPanel,
  ReviewApprovalQueue,
  SimulationEnginePanel,
  PublishingMonitorPanel,
  SuppressionCenterPanel,
} from '../../components/cataloglifecycle';
import { Card, KpiCard } from '../../components/ui';

export function CatalogOperationsPage() {
  const dashboard = buildCatalogOpsDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_CO_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Catalog Operations Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Product lifecycle, release management, version control, review & approval, simulation, publishing monitor, and rollback — the enterprise deployment platform for every catalog asset.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Lifecycle Stages" value={dashboard.lifecycle_stages.toString()} />
        <KpiCard label="Release Contents" value={dashboard.release_contents.toString()} />
        <KpiCard label="Review Types" value={dashboard.review_types.toString()} />
        <KpiCard label="Simulation Metrics" value={dashboard.simulation_metrics.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Catalog Explorer</h2>
        <Card className="mt-4" padding="sm">
          <CatalogExplorerPanel features={EXPLORER_FEATURES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Lifecycle</h2>
        <div className="mt-4">
          <LifecycleStageTracker stages={LIFECYCLE_STAGES} currentStage="Review" />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Release Packages</h2>
          <p className="mt-1 text-sm text-slate-500">{RELEASE_CONTENTS.join(' · ')}</p>
          <div className="mt-4">
            <ReleasePackageCard releases={DEMO_RELEASES} />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Version Control</h2>
          <p className="mt-1 text-sm text-slate-500">{VERSION_FEATURES.join(' · ')}</p>
          <div className="mt-4">
            <VersionControlPanel />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Review & Approval Queue</h2>
        <Card className="mt-4" padding="sm">
          <ReviewApprovalQueue items={DEMO_REVIEW_QUEUE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Simulation Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{SIMULATION_METRICS.join(' · ')}</p>
        <div className="mt-4">
          <SimulationEnginePanel scores={DEMO_SIMULATION} />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Publishing Monitor</h2>
          <p className="mt-1 text-sm text-slate-500">{PUBLISH_JOB_STATES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <PublishingMonitorPanel jobs={DEMO_PUBLISH_JOBS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Suppression Center</h2>
          <p className="mt-1 text-sm text-slate-500">{SUPPRESSION_FEATURES.join(' · ')}</p>
          <div className="mt-4">
            <SuppressionCenterPanel items={DEMO_SUPPRESSIONS} />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Publishing Advisor</h2>
        <Card className="mt-4" padding="sm" >
          <ul className="space-y-2">
            {DEMO_ADVISOR_INSIGHTS.map((insight) => (
              <li key={insight} className="flex gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span className="text-brand-500">→</span>{insight}
              </li>
            ))}
          </ul>
        </Card>
        <div className="mt-3 flex flex-wrap gap-2">
          {ADVISOR_RECOMMENDATIONS.map((r) => (
            <span key={r} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{r}</span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Marketplace Validation</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {VALIDATION_CHECKS.map((c) => (
              <li key={c}>✓ {c}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Draft Management</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {DRAFT_CAPABILITIES.map((d) => (
              <span key={d} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{d}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Scheduling</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {SCHEDULING_MODES.map((m) => (
              <li key={m}>✓ {m}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Rollback</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {ROLLBACK_SCOPES.map((r) => (
              <span key={r} className="rounded-lg border border-error-200 bg-error-50 px-2 py-1 text-xs text-error-700 dark:border-error-900 dark:bg-error-950 dark:text-error-300">{r}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Bulk Operations</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {BULK_OPERATIONS.map((op) => (
            <span key={op} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{op}</span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Review Types</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {REVIEW_TYPES.map((t) => (
              <li key={t}>✓ {t}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {COLLABORATION_FEATURES.map((f) => (
              <li key={f}>✓ {f}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <li key={s}>✓ {s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Analytics Metrics</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ANALYTICS_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{m}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/uxp/UXP-00007_CATALOG_OPERATIONS_PLATFORM.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/cataloglifecycle/</code>
        {' · '}
        Pipeline: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/publishing/release_pipeline.go</code>
      </p>
    </div>
  );
}
